import React, {Component, ReactNode} from 'react'
import {ATTENDANCE_STATUS, AVAILABLE_OR_NOT, MESSAGE_TYPE, ROLE_FOR_ADMINISTRATOR} from '../../CodeMaster'
import {Nullable} from '../../app/TypeHelpers'
import {Moment} from 'moment'
import {
    Box,
    DialogActions,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    SortDirection,
} from '@mui/material';
import {formatDateTimeLocal} from '../../formatters/formatMoment'
import {AdminPageHeadingArea} from '../shared/pageBaseComponents/AdminPageHeadingArea'
import {AdminDefaultMediumButton, AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import AdminMeetingAttendanceStatusTag from './AdminMeetingAttendanceStatusTag'
import styled from 'styled-components'
import {CenterRowFlexContainer, FullWidthRowFlexContainer} from '../../styledComponents/FlexComponents'
import {StyledPre} from '../../styledComponents/adminPage/StyledPre'
import {Colors} from '../../styledComponents/Constants'
import {MeetingHelper} from './utils/MeetingHelper'
import {EditLink, StyledLink} from '../../styledComponents/adminPage/Links'
import {HKConstants} from '../../HKConstants'
import PPSelect, {OptionProps} from '../shared/forms/PPSelect'
import PPInput from '../shared/forms/PPInput'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {StyledDiv} from '../../styledComponents/StyledTypeDefinitions'
import PPTextField from '../shared/forms/PPTextField'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {MeetingRepositoryForAdmin} from './MeetingRepositoryForAdmin'
import {
    AsyncCommunicationProps,
    CallBackHttpErrorHandler,
    withAsyncCommunication,
} from '../shared/hoc/withAsyncCommunication'
import {
    HeadingAreaMessageComponent,
    HeadingAreaMessageParams,
} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import MeetingAttendanceResponse from './model/MeetingAttendanceResponse'
import {Room} from './model/Room'
import {AssocDirectorHelper} from './utils/AssocDirectorHelper'
import {SelectChangeEvent} from '@mui/material/Select'

export interface MeetingAttendanceRecord {
    attendanceId: Nullable<number>
    attendanceStatus: ATTENDANCE_STATUS
    adminId: string
    familyName: string
    firstName: string
    roomList: Room[]
    roomNumberSortKey: string
    responseDateTime: Nullable<Moment>
    proxyUserName: Nullable<string>
    proxyUserRelationship: Nullable<string>
    note: Nullable<string>
    agentRespondentAdminId: Nullable<string>
    agentRespondentRoleName: string
    agentRespondentName: string
    version: Nullable<number>
    isWithinTerm: boolean
    hasMailAddress: boolean
}

interface Props {
    meetingRepositoryForAdmin: MeetingRepositoryForAdmin
    meetingAttendanceRecords: MeetingAttendanceRecord[]
    condominiumCd: string
    meetingId: number
    proxyAttendanceAvailability: AVAILABLE_OR_NOT
    isPastMeeting: boolean
    onClose: () => void
    eventEmitter: EventEmitterExAdmin
}

interface State {
    meetingAttendanceRecords: MeetingAttendanceRecord[]
    roomNumberSortDirection: SortDirection
    attendanceStatusSortDirection: SortDirection
    editBaseMeetingAttendanceRecord: Nullable<MeetingAttendanceRecord>
    editMeetingAttendanceStatus: Nullable<ATTENDANCE_STATUS>
    editProxyUserName: Nullable<string>
    editProxyUserRelationship: Nullable<string>
    editNote: Nullable<string>
    errorHeadingAreaMessageParams: Nullable<HeadingAreaMessageParams>
}

type Direction = 'asc' | 'desc'

class AdminMeetingAttendanceDialog extends Component<Props & AsyncCommunicationProps, State> {
    eventEmitterForDialog: EventEmitterExAdmin

    DEFAULT_ROOM_NUMBER_SORT_DIRECTION: Direction = 'asc'
    DEFAULT_ATTENDANCE_STATUS_SORT_DIRECTION: Direction = 'asc'

    constructor(props: Readonly<Props & AsyncCommunicationProps>) {
        super(props)

        this.eventEmitterForDialog = new EventEmitterExAdmin()

        const sortedRecords =
            this.sortRoomNumber(props.meetingAttendanceRecords, this.DEFAULT_ROOM_NUMBER_SORT_DIRECTION)

        this.state = {
            meetingAttendanceRecords: sortedRecords,
            roomNumberSortDirection: this.DEFAULT_ROOM_NUMBER_SORT_DIRECTION,
            attendanceStatusSortDirection: false,
            editBaseMeetingAttendanceRecord: null,
            editMeetingAttendanceStatus: null,
            editProxyUserName: null,
            editProxyUserRelationship: null,
            editNote: null,
            errorHeadingAreaMessageParams: null,
        }

        this.handleClickRoomNumberHeader = this.handleClickRoomNumberHeader.bind(this)
        this.handleClickAttendanceStatusHeader = this.handleClickAttendanceStatusHeader.bind(this)
        this.handleChangeAttendanceStatus = this.handleChangeAttendanceStatus.bind(this)
        this.handleChangeProxyUserName = this.handleChangeProxyUserName.bind(this)
        this.handleChangeProxyUserRelationship = this.handleChangeProxyUserRelationship.bind(this)
        this.handleChangeNote = this.handleChangeNote.bind(this)
        this.handleClickCancelButton = this.handleClickCancelButton.bind(this)
        this.handleClickAgentResponseButton = this.handleClickAgentResponseButton.bind(this)
    }

    sortRoomNumber(records: MeetingAttendanceRecord[], direction: Direction): MeetingAttendanceRecord[] {
        const sortedRecords = [...records]
        sortedRecords.sort((a, b, sortDirection = direction) => {
            const keyA = a.roomNumberSortKey.toUpperCase()
            const keyB = b.roomNumberSortKey.toUpperCase()
            const sig = sortDirection === 'desc' ? -1 : 1
            let result: number
            if (keyA < keyB) {
                result = -1
            } else if (keyA > keyB) {
                result = 1
            } else {
                result = 0
            }

            return result * sig
        })

        return sortedRecords
    }

    sortAttendanceStatus(records: MeetingAttendanceRecord[], direction: Direction): MeetingAttendanceRecord[] {
        const sortedRecords = [...records]
        sortedRecords.sort((a, b, sortDirection = direction) => {
            const aVal = Number(a.attendanceStatus.displayOrder)
            const bVal = Number(b.attendanceStatus.displayOrder)
            const sig = sortDirection === 'desc' ? -1 : 1

            return (aVal - bVal) * sig
        })

        return sortedRecords
    }

    handleClickRoomNumberHeader(): void {
        const {meetingAttendanceRecords, roomNumberSortDirection} = this.state
        const nextDirection = this.flipSortDirection(
            roomNumberSortDirection,
            this.DEFAULT_ROOM_NUMBER_SORT_DIRECTION,
        )
        const sortedRecords = this.sortRoomNumber(meetingAttendanceRecords, nextDirection)

        this.setState({
            meetingAttendanceRecords: sortedRecords,
            roomNumberSortDirection: nextDirection,
            attendanceStatusSortDirection: false,
        })
    }

    handleClickAttendanceStatusHeader(): void {
        const {meetingAttendanceRecords, attendanceStatusSortDirection} = this.state
        const nextDirection = this.flipSortDirection(
            attendanceStatusSortDirection,
            this.DEFAULT_ATTENDANCE_STATUS_SORT_DIRECTION,
        )
        const sortedRecords = this.sortAttendanceStatus(meetingAttendanceRecords, nextDirection)

        this.setState({
            meetingAttendanceRecords: sortedRecords,
            roomNumberSortDirection: false,
            attendanceStatusSortDirection: nextDirection,
        })
    }

    handleClickProxyResponse = (editMeetingAttendanceRecord: MeetingAttendanceRecord) => () => {
        let attendanceStatus = editMeetingAttendanceRecord.attendanceStatus
        if (this.props.proxyAttendanceAvailability === AVAILABLE_OR_NOT.UNAVAILABLE
            && attendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND) {
            attendanceStatus = ATTENDANCE_STATUS.UNANSWERED
        }

        this.setState({
            editBaseMeetingAttendanceRecord: editMeetingAttendanceRecord,
            editMeetingAttendanceStatus: attendanceStatus,
            editProxyUserName: editMeetingAttendanceRecord.proxyUserName,
            editProxyUserRelationship: editMeetingAttendanceRecord.proxyUserRelationship,
            editNote: editMeetingAttendanceRecord.note,
        })
    }

    handleChangeAttendanceStatus(event: SelectChangeEvent<unknown>): void {
        const attendanceStatus = ATTENDANCE_STATUS.codeToEnumConstants(String(event.target.value))
        this.setState({
            editMeetingAttendanceStatus: attendanceStatus,
        })

    }

    handleChangeProxyUserName(event: React.ChangeEvent<HTMLInputElement>): void {
        const proxyUserName = event.target.value
        this.setState({editProxyUserName: proxyUserName})
    }

    handleChangeProxyUserRelationship(event: React.ChangeEvent<HTMLInputElement>): void {
        const proxyUserRelationship = event.target.value
        this.setState({editProxyUserRelationship: proxyUserRelationship})
    }

    handleChangeNote(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        const note = event.target.value
        this.setState({editNote: note})
    }

    handleClickCancelButton(): void {
        this.setState({
            editBaseMeetingAttendanceRecord: null,
            errorHeadingAreaMessageParams: null,
        })
        this.eventEmitterForDialog.clearBroadcastedErrors()
    }

    async handleClickAgentResponseButton(): Promise<void> {
        const {
            editBaseMeetingAttendanceRecord,
            editMeetingAttendanceStatus,
            editProxyUserName,
            editProxyUserRelationship,
            editNote,
            meetingAttendanceRecords,
        } = this.state

        const {
            eventEmitter,
            condominiumCd,
            meetingId,
            meetingRepositoryForAdmin,
            asyncCallFunction,
        } = this.props

        if (!editBaseMeetingAttendanceRecord || !editMeetingAttendanceStatus) {
            return
        }

        let proxyUserName: Nullable<string> = null
        let proxyUserRelationship: Nullable<string> = null
        if (editMeetingAttendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND) {
            proxyUserName = editProxyUserName
            proxyUserRelationship = editProxyUserRelationship
        }

        let agentAttendanceResponseResult: Nullable<MeetingAttendanceResponse>
        await eventEmitter.showLoading()
        try {
            if (!editBaseMeetingAttendanceRecord.attendanceId) {
                agentAttendanceResponseResult = await asyncCallFunction(
                    meetingRepositoryForAdmin.registerAttendance.bind(
                        meetingRepositoryForAdmin,
                        condominiumCd,
                        meetingId,
                        {
                            attendanceStatus: editMeetingAttendanceStatus,
                            proxyUserName: proxyUserName,
                            proxyUserRelationship: proxyUserRelationship,
                            note: editNote,
                            respondentAdminId: editBaseMeetingAttendanceRecord.adminId,
                        }),
                    this.getAgentAttendanceErrorHandler(),
                )
            } else {
                if (!editBaseMeetingAttendanceRecord.version) {
                    return
                }
                agentAttendanceResponseResult = await asyncCallFunction(
                    meetingRepositoryForAdmin.updateAttendance.bind(
                        meetingRepositoryForAdmin,
                        condominiumCd,
                        meetingId,
                        editBaseMeetingAttendanceRecord.attendanceId,
                        {
                            attendanceStatus: editMeetingAttendanceStatus,
                            proxyUserName: proxyUserName,
                            proxyUserRelationship: proxyUserRelationship,
                            note: editNote,
                            respondentAdminId: editBaseMeetingAttendanceRecord.adminId,
                            version: editBaseMeetingAttendanceRecord.version,
                        }),
                    this.getAgentAttendanceErrorHandler(),
                )
            }
            if (agentAttendanceResponseResult) {
                const afterRegisterMeetingAttendanceRecords = meetingAttendanceRecords
                    .map(attendanceRecord => {
                        if (attendanceRecord.adminId !== editBaseMeetingAttendanceRecord.adminId) {
                            return attendanceRecord
                        } else {
                            return {
                                ...editBaseMeetingAttendanceRecord,
                                attendanceId: agentAttendanceResponseResult!.attendanceId,
                                attendanceStatus: agentAttendanceResponseResult!.attendanceStatus,
                                responseDateTime: agentAttendanceResponseResult!.responseDateTime,
                                proxyUserName: agentAttendanceResponseResult!.proxyUserName,
                                proxyUserRelationship: agentAttendanceResponseResult!.proxyUserRelationship,
                                note: agentAttendanceResponseResult!.note,
                                agentRespondentAdminId: agentAttendanceResponseResult!.agentRespondentAdminId,
                                agentRespondentRoleName: agentAttendanceResponseResult!.agentRespondentRoleName,
                                agentRespondentName: agentAttendanceResponseResult!.agentRespondentName,
                                version: agentAttendanceResponseResult!.version,
                            }

                        }
                    })
                this.setState({
                    editBaseMeetingAttendanceRecord: null,
                    meetingAttendanceRecords: afterRegisterMeetingAttendanceRecords,
                    errorHeadingAreaMessageParams: null,
                })
                this.eventEmitterForDialog.clearBroadcastedErrors()
            }
        } finally {
            eventEmitter.closeLoading()
        }

    }

    private getAgentAttendanceErrorHandler(): CallBackHttpErrorHandler {
        return async (httpErrorOrNetworkError) => {
            if (httpErrorOrNetworkError.responseBody.requestError !== null) {
                await httpErrorOrNetworkError.broadcast(this.eventEmitterForDialog)
                const title = httpErrorOrNetworkError.responseBody.requestError.title
                const message = httpErrorOrNetworkError.responseBody.requestError.message
                const headingAreaMessageParams: HeadingAreaMessageParams = {
                    messageLevel: MESSAGE_TYPE.ERROR,
                    title: title,
                    detail: message,
                }
                this.setState({
                    errorHeadingAreaMessageParams: headingAreaMessageParams,
                })
            }
        }
    }

    flipSortDirection(sortDirection: SortDirection, defaultDirection: Direction): Direction {
        if (sortDirection === false) {
            return defaultDirection
        }
        return sortDirection === 'asc' ? 'desc' : 'asc'
    }

    judgeDisabledSubmitButton(
        editBaseMeetingAttendanceRecord: Nullable<MeetingAttendanceRecord>,
        editMeetingAttendanceStatus: Nullable<ATTENDANCE_STATUS>,
        editProxyUserName: Nullable<string>,
        editProxyUserRelationship: Nullable<string>,
        editNote: Nullable<string>,
    ): boolean {
        if (editBaseMeetingAttendanceRecord) {
            if (editMeetingAttendanceStatus === ATTENDANCE_STATUS.PROXY_ATTEND) {
                if (!(editProxyUserName?.trim()) || !(editProxyUserRelationship?.trim())) {
                    return true
                }
                if (editBaseMeetingAttendanceRecord.proxyUserName !== editProxyUserName) {
                    return false
                }
                if (editBaseMeetingAttendanceRecord.proxyUserRelationship !== editProxyUserRelationship) {
                    return false
                }
            }
            if (editBaseMeetingAttendanceRecord.attendanceStatus !== editMeetingAttendanceStatus) {
                return false
            }
            if (editBaseMeetingAttendanceRecord.note !== editNote) {
                return false
            }
        }
        return true
    }

    renderAgentResponseForm(): ReactNode {
        const {
            proxyAttendanceAvailability,
        } = this.props
        const {
            editBaseMeetingAttendanceRecord,
            editMeetingAttendanceStatus,
            editProxyUserName,
            editProxyUserRelationship,
            editNote,
        } = this.state

        const attendanceStatusList: OptionProps[] = ATTENDANCE_STATUS.values()
            .filter(status => proxyAttendanceAvailability === AVAILABLE_OR_NOT.AVAILABLE
                || status !== ATTENDANCE_STATUS.PROXY_ATTEND)

        const disabledSubmitButton: boolean = this.judgeDisabledSubmitButton(
            editBaseMeetingAttendanceRecord,
            editMeetingAttendanceStatus,
            editProxyUserName,
            editProxyUserRelationship,
            editNote,
        )

        return <Box bgcolor={'rgb(254, 246, 233)'} mr={'16px'}
                    padding={'8px 12px 16px 12px'}>
            <FullWidthRowFlexContainer>
                <Box width={120}>
                    <label>
                        <LabelTitle>
                            出欠
                        </LabelTitle>
                        <PPSelect
                            data={attendanceStatusList}
                            value={editMeetingAttendanceStatus
                                ? editMeetingAttendanceStatus.value
                                : ATTENDANCE_STATUS.UNANSWERED.value}
                            onChange={this.handleChangeAttendanceStatus}
                            fullWidth={true}
                        />
                    </label>
                </Box>
                {editMeetingAttendanceStatus ===
                    ATTENDANCE_STATUS.PROXY_ATTEND &&
                    <>
                        <Box ml={'16px'} flexGrow={1}>
                            <label>
                                <LabelTitle>
                                    代理人氏名
                                </LabelTitle>
                                <PPInput
                                    inputProps={{
                                        maxLength: 25,
                                    }}
                                    fullWidth
                                    onChange={this.handleChangeProxyUserName}
                                    eventEmitter={this.eventEmitterForDialog}
                                    eventKeys={['proxyUserName']}
                                    value={editProxyUserName || ''}
                                />
                            </label>
                        </Box>
                        <Box width={'96px'} ml={'8px'}>
                            <label>
                                <LabelTitle>
                                    続柄
                                </LabelTitle>
                                <PPInput
                                    inputProps={{
                                        maxLength: 15,
                                    }}
                                    fullWidth
                                    onChange={
                                        this.handleChangeProxyUserRelationship
                                    }
                                    eventEmitter={this.eventEmitterForDialog}
                                    eventKeys={['proxyUserRelationship']}
                                    value={editProxyUserRelationship || ''}
                                />
                            </label>
                        </Box>
                    </>
                }
            </FullWidthRowFlexContainer>
            <Box mt={'8px'}>
                <label>
                    <LabelTitle>
                        連絡事項
                    </LabelTitle>
                    <StyledTextField
                        inputProps={{
                            maxLength: 100,
                        }}
                        multiline
                        fullWidth
                        onChange={this.handleChangeNote}
                        eventEmitter={this.eventEmitterForDialog}
                        eventKeys={['note']}
                        value={editNote || ''}
                    />
                </label>
            </Box>
            <Box mt={'16px'}>
                <StyledFlexContainerForButton>
                    <AgentResponseCancelLink onClick={this.handleClickCancelButton}>
                        キャンセル
                    </AgentResponseCancelLink>
                    <AgentResponseButton
                        disabled={disabledSubmitButton}
                        onClick={this.handleClickAgentResponseButton}
                    >
                        代行回答する
                    </AgentResponseButton>
                </StyledFlexContainerForButton>
            </Box>
        </Box>
    }

    renderTableCellsAttendanceResponseDetail(rec: MeetingAttendanceRecord): ReactNode {
        const {
            editBaseMeetingAttendanceRecord,
        } = this.state

        if (editBaseMeetingAttendanceRecord) {
            if (rec.adminId === editBaseMeetingAttendanceRecord.adminId) {
                return <StyledTableCell colSpan={2}>
                    {this.renderAgentResponseForm()}
                </StyledTableCell>
            }

            return <>
                <StyledTableCell>
                    {this.renderAdditionalInfoNode(rec)}
                </StyledTableCell>
                <StyledTableCell/>
            </>
        } else {
            if (!this.props.isPastMeeting &&
                judgeAuthorityCheck(HKConstants.CMN.AUTH.MANAGE_ATTENDANCE_ASSOC_DIRECTOR)) {
                return <>
                    <StyledTableCell>
                        {this.renderAdditionalInfoNode(rec)}
                    </StyledTableCell>
                    <EditLinkTableCell>
                        <StyledEditLink onClick={this.handleClickProxyResponse(rec)}>
                            代行回答
                        </StyledEditLink>
                    </EditLinkTableCell>
                </>
            }

            return <StyledTableCell colSpan={2}>
                {this.renderAdditionalInfoNode(rec)}
            </StyledTableCell>
        }
    }

    renderAdditionalInfoNode(rec: MeetingAttendanceRecord): ReactNode {
        return <NoteArea>
            <div>
                {
                    rec.proxyUserName && rec.proxyUserRelationship &&
                    <ProxyUserName>代理出席者：{rec.proxyUserName}（{rec.proxyUserRelationship}）</ProxyUserName>
                }
                <StyledPre>{rec.note || ''}</StyledPre>
            </div>
            {rec.agentRespondentAdminId &&
                <AgentRespondentName>
                    <StyledSpan>代行回答者： {rec.agentRespondentName}</StyledSpan>
                    <StyledSpan>（{rec.agentRespondentRoleName}）</StyledSpan>
                </AgentRespondentName>
            }
        </NoteArea>
    }

    renderTable(): ReactNode {
        const {
            roomNumberSortDirection,
            attendanceStatusSortDirection,
        } = this.state

        return <StyledTable stickyHeader>
            <TableHead>
                <TableRow>
                    <StyledTableHeaderCell width={'45px'}/>
                    <StyledTableHeaderCell width={'200px'}>
                        <TableSortLabel
                            active={roomNumberSortDirection !== false}
                            direction={roomNumberSortDirection || this.DEFAULT_ROOM_NUMBER_SORT_DIRECTION}
                            onClick={this.handleClickRoomNumberHeader}
                        >
                            氏名／部屋番号
                        </TableSortLabel>
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell width={'100px'}>
                        <TableSortLabel
                            active={attendanceStatusSortDirection !== false}
                            direction={attendanceStatusSortDirection || this.DEFAULT_ATTENDANCE_STATUS_SORT_DIRECTION}
                            onClick={this.handleClickAttendanceStatusHeader}
                        >
                            回答状況
                        </TableSortLabel>
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell width={'390px'}/>
                    <StyledTableHeaderCell width={'164px'}/>
                </TableRow>
            </TableHead>
            <TableBody>
                {
                    this.state.meetingAttendanceRecords
                        .map((rec, idx) => <TableRow key={`row-${idx}`}>
                            <StyledTableCell>
                                {
                                    MeetingHelper.getUserIconNodeByResidentStatus(
                                        rec.isWithinTerm,
                                        rec.hasMailAddress,
                                        ROLE_FOR_ADMINISTRATOR.ASSOC,
                                        rec.roomList,
                                        'medium',
                                    )
                                }
                            </StyledTableCell>
                            <StyledTableCell>
                                <div>
                                    {AssocDirectorHelper.getDisplayUserNameByResidentStatus(
                                        rec.familyName, rec.firstName, rec.roomList)}
                                </div>
                                <RoomNumberText>
                                    {MeetingHelper.getDisplayRoomListByResidentStatus(rec.roomList)}
                                </RoomNumberText>
                            </StyledTableCell>
                            <StyledTableCell align={'center'}>
                                <ResponseStatusArea>
                                    <AdminMeetingAttendanceStatusTag
                                        meetingAttendanceStatus={rec.attendanceStatus}/>
                                    <ResponseDatetime>
                                        {
                                            rec.responseDateTime ? formatDateTimeLocal(rec.responseDateTime) : ''
                                        }
                                    </ResponseDatetime>
                                </ResponseStatusArea>
                            </StyledTableCell>
                            {this.renderTableCellsAttendanceResponseDetail(rec)}
                        </TableRow>)
                }
            </TableBody>
        </StyledTable>
    }

    render(): ReactNode {
        return <>
            <StyledDialogTitle><AdminPageHeadingArea title={'出欠回答状況'}/></StyledDialogTitle>
            {this.state.errorHeadingAreaMessageParams &&
                <HeadingAreaMessageComponent params={this.state.errorHeadingAreaMessageParams}/>
            }
            <StyledTableContainer>
                {this.renderTable()}
            </StyledTableContainer>
            <StyledDialogActions>
                <CenteringFullWidthContainer>
                    <AdminDefaultMediumButton onClick={this.props.onClose}>閉じる</AdminDefaultMediumButton>
                </CenteringFullWidthContainer>
            </StyledDialogActions>
        </>
    }
}

export default withAsyncCommunication(AdminMeetingAttendanceDialog)

const StyledDialogTitle = styled(DialogTitle)`
  &.MuiDialogTitle-root {
    padding: 16px 12px 10px 12px;
  }
`

const StyledTableContainer = styled(TableContainer)`
  &.MuiTableContainer-root {
    padding: 0 12px;
    min-width: 600px;
  }
`

const StyledTable = styled(Table)`
  &.MuiTable-root {
    border-style: solid;
    border-color: rgb(217, 217, 217);
    border-width: 0 1px;
    height: 100%;
  }
`

const StyledTableHeaderCell = styled(TableCell)<{ width?: string }>`
  &.MuiTableCell-root {
    padding: 5px 0 5px 20px;
    font-size: 0.85em;
    background-color: rgb(241, 241, 241);
    width: ${props => props.width ?? 'auto'};
    border-style: solid;
    border-color: rgb(217, 217, 217);
    border-width: 1px 0;
  }
`

const StyledTableCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding: 16px 10.5px;
    font-size: 1.0em;
  }
`

const StyledDialogActions = styled(DialogActions)`
  &.MuiDialogActions-root {
    padding: 18px 0;
  }
`

const CenteringFullWidthContainer = styled(CenterRowFlexContainer)`
  width: 100%
`

const RoomNumberText = styled.div`
  padding-right: 16px;
  font-size: 12px;
  color: ${Colors.AccentDark};
`

const ResponseStatusArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const NoteArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const ResponseDatetime = styled.div`
  font-size: 12px;
  color: ${Colors.AccentDark};
`

const ProxyUserName = styled.div`
  margin-bottom: 4px;
`

const StyledSpan = styled.span`
  display: inline-block;
`

const AgentRespondentName = styled.div`
  font-size: 12px;
  color: ${Colors.AccentDark};
  text-align: right;
  word-break: keep-all;
  margin-top: 8px;
`

const EditLinkTableCell = styled(TableCell)`
  &.MuiTableCell-root {
    text-align: right;
    padding-right: 24px;
  }
`

const StyledEditLink = styled(EditLink)`
  .MuiSvgIcon-root {
    font-size: 16px;
    margin-right: 4px;
  }
`

const LabelTitle = styled.div`
  font-size: 12px;
  color: ${Colors.AccentDark};
`

const AgentResponseCancelLink = styled(StyledLink)`
  &.MuiLink-button {
    margin: 0 32px
  }
`

const AgentResponseButton = styled(AdminPrimaryButton)`
  &.MuiButton-root {
    padding: 5px 28px
  }
`

const StyledFlexContainerForButton: StyledDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  flex-wrap: nowrap;
`

const StyledTextField = styled(PPTextField)`
  textarea {
    padding-top: 6px;
    padding-bottom: 7px;
  }
`
